'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { CLPPurchaseOption, useClpPurchaseContext, useClpSelectedOption } from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import { publishEvent } from '@udemy-v2/event-tracking';
import styles from './subscription-purchase-content.module.scss';
import { useCommonData } from '@udemy-v2/common-data';
import { useFormatNumber, useI18n } from '@udemy-v2/i18n';
import { useClpNoticeContext } from '@/features/course-landing-page/context/clp-notice-context-provider.react-component';
import { aggregateCollectionStats, FALLBACK_COLLECTION_STATS, PLAN_SELECTION_CHECKOUT_URL, PlanPeriodStyle, SubscribeFromPageCookie, SubscriptionCTA, SubscriptionCTAClickEvent, SubscriptionLearnMoreEvent, SubscriptionOfferPresented, usePersonalPlanCollectionStats } from '@udemy-v2/react-subscription-browse';
import { SubscriptionPlanProductType } from '@udemy-v2/subscription-graphql';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
import { useClpSubscriptionContext } from '@/features/course-landing-page/context/clp-subscription-context.react-component';
import { useDeviceType } from '@udemy-v2/hooks';
import { useFeatures } from '@udemy-v2/react-experimentation';
const UI_REGION = 'purchase_section_tab0';
const PERSONAL_PLAN_COURSE_COUNT = 27000;
const DEFAULT_PERSONAL_PLAN_LEARNER_COUNT = 559000;
const SUBSCRIPTION_ATTRIBUTION_PATHS = ['/personal-plan', '/pricing'];
export interface SubscriptionPurchaseContentProps {
  personalPlanLearnerCount?: number;
}
export const SubscriptionPurchaseContent = t0 => {
  const $ = _c(63);
  const {
    personalPlanLearnerCount
  } = t0 === undefined ? {} : t0;
  const {
    gettext,
    interpolate
  } = useI18n();
  const {
    formatNumber
  } = useFormatNumber();
  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";
  const commonData = useCommonData();
  const {
    isLoading,
    unwrappedConsumerSubscriptionPlan,
    subscriptionCTAConfiguration,
    subscriptionTiersEnabled
  } = useClpPurchaseContext();
  const {
    selectedPurchaseOption
  } = useClpSelectedOption();
  const {
    isCourseInValuePlanCatalog,
    unwrappedPersonalPlan,
    subscribedProductType
  } = useClpSubscriptionContext();
  const isGwgStandaloneSubscriber = subscribedProductType === SubscriptionPlanProductType.GrowWithGoogle;
  const clpNoticeContext = useClpNoticeContext();
  const course = useClpCourse();
  const subscriptionPriceBoxNotice = clpNoticeContext?.subscriptionPriceBoxNoticeData;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["pp_value_prop_update"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    featureVariantAssignments
  } = useFeatures(t1);
  let t2;
  if ($[1] !== featureVariantAssignments) {
    t2 = featureVariantAssignments.get("pp_value_prop_update")?.configuration;
    $[1] = featureVariantAssignments;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const ppValuePropConfig = t2;
  const learnMoreInNewTab = ppValuePropConfig?.variant1 ?? false;
  const learnMoreInModal = ppValuePropConfig?.variant2 ?? false;
  const showPersonalPlanValueProp = learnMoreInNewTab || learnMoreInModal;
  const personalPlanValuePropText = interpolate(gettext("Join %(learner_count)s learners on Personal Plan"), {
    learner_count: formatNumber(personalPlanLearnerCount ?? DEFAULT_PERSONAL_PLAN_LEARNER_COUNT)
  }, true);
  let t3;
  if ($[3] !== gettext) {
    t3 = gettext("Subscribe to Udemy\u2019s top courses");
    $[3] = gettext;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const defaultTitle = t3;
  const title = subscriptionPriceBoxNotice?.title ?? defaultTitle;
  const contentCollections = unwrappedConsumerSubscriptionPlan?.contentCollections;
  const subscriptionPlanStats = contentCollections ? aggregateCollectionStats(contentCollections.map(_temp)) : FALLBACK_COLLECTION_STATS;
  const personalPlanStats = usePersonalPlanCollectionStats();
  const personalPlanCourseCount = personalPlanStats && "numberOfCourses" in personalPlanStats ? personalPlanStats.numberOfCourses : isGwgStandaloneSubscriber && contentCollections ? subscriptionPlanStats.numberOfCourses : PERSONAL_PLAN_COURSE_COUNT;
  const tiersCourseCount = personalPlanCourseCount;
  const defaultSubtitle = subscriptionTiersEnabled && isCourseInValuePlanCatalog ? interpolate(gettext("Get this premium course, plus %(courses_count)s+ of our top-rated courses with Personal Plan."), {
    courses_count: formatNumber(tiersCourseCount)
  }, true) : subscriptionTiersEnabled ? interpolate(gettext("Get this premium course, plus %(courses_count)s+ of our top-rated courses with Personal Plan."), {
    courses_count: formatNumber(tiersCourseCount)
  }, true) : interpolate(gettext("Get this course, plus %(courses_count)s+ of our top-rated courses, with Personal Plan."), {
    courses_count: formatNumber(subscriptionPlanStats.numberOfCourses)
  }, true);
  const description = subscriptionPriceBoxNotice?.subtitle ?? defaultSubtitle;
  let t4;
  if ($[5] !== gettext || $[6] !== subscriptionPriceBoxNotice?.subtitle_link_text) {
    t4 = subscriptionPriceBoxNotice?.subtitle_link_text ?? gettext("Learn more");
    $[5] = gettext;
    $[6] = subscriptionPriceBoxNotice?.subtitle_link_text;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const learnMoreText = t4;
  const subscriptionContextLearnMoreUrl = unwrappedConsumerSubscriptionPlan?.urlLearnMore ?? "https://udemy.com/personal-plan/";
  const learnMoreUrl = subscriptionPriceBoxNotice?.subtitle_link_url ?? subscriptionContextLearnMoreUrl;
  const learnMoreTarget = learnMoreInNewTab || subscriptionPriceBoxNotice?.subtitle_link_target === "new_tab" ? "_blank" : "_self";
  let t5;
  if ($[8] !== course.id || $[9] !== learnMoreUrl || $[10] !== unwrappedConsumerSubscriptionPlan) {
    t5 = () => {
      if (SUBSCRIPTION_ATTRIBUTION_PATHS.some(path => learnMoreUrl.includes(path))) {
        SubscribeFromPageCookie.set("course_landing_page", parseInt(course.id, 10));
      }
      if (unwrappedConsumerSubscriptionPlan) {
        publishEvent(new SubscriptionLearnMoreEvent({
          planId: unwrappedConsumerSubscriptionPlan.id,
          planType: unwrappedConsumerSubscriptionPlan.productType,
          uiRegion: UI_REGION,
          planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds
        }));
      }
    };
    $[8] = course.id;
    $[9] = learnMoreUrl;
    $[10] = unwrappedConsumerSubscriptionPlan;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleLearnMoreClick = t5;
  let t6;
  if ($[12] !== course.id || $[13] !== unwrappedConsumerSubscriptionPlan) {
    t6 = () => {
      SubscribeFromPageCookie.set("course_landing_page", parseInt(course.id, 10));
      if (unwrappedConsumerSubscriptionPlan) {
        publishEvent(new SubscriptionCTAClickEvent({
          planId: unwrappedConsumerSubscriptionPlan.id,
          planType: unwrappedConsumerSubscriptionPlan.productType,
          ctaType: unwrappedConsumerSubscriptionPlan.trialPeriodDays ? "trial" : "paid",
          uiRegion: UI_REGION,
          courseId: parseInt(course.id, 10),
          planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds
        }));
      }
    };
    $[12] = course.id;
    $[13] = unwrappedConsumerSubscriptionPlan;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleCtaClick = t6;
  const showPPPrice = subscriptionTiersEnabled && isCourseInValuePlanCatalog && unwrappedPersonalPlan;
  let t7;
  if ($[15] !== commonData?.user?.country || $[16] !== course.frontendTrackingId || $[17] !== course.id || $[18] !== unwrappedConsumerSubscriptionPlan) {
    t7 = unwrappedConsumerSubscriptionPlan ? () => {
      publishEvent(new SubscriptionOfferPresented({
        subscriptionPlan: unwrappedConsumerSubscriptionPlan,
        productType: unwrappedConsumerSubscriptionPlan.productType,
        countryCode: commonData?.user?.country ?? "",
        courseId: parseInt(course.id, 10),
        courseTrackingId: course.frontendTrackingId ?? null,
        uiRegion: UI_REGION
      }));
    } : undefined;
    $[15] = commonData?.user?.country;
    $[16] = course.frontendTrackingId;
    $[17] = course.id;
    $[18] = unwrappedConsumerSubscriptionPlan;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const handleOfferImpression = t7;
  if (!unwrappedConsumerSubscriptionPlan) {
    return null;
  }
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = classNames(styles.title, "ud-heading-serif-xxl");
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== title) {
    t9 = <div className={t8}>{title}</div>;
    $[21] = title;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = classNames(styles.description, "ud-text-sm");
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== gettext || $[25] !== learnMoreInNewTab || $[26] !== learnMoreText) {
    t11 = learnMoreInNewTab ? gettext("Learn more about Udemy subscriptions") : learnMoreText;
    $[24] = gettext;
    $[25] = learnMoreInNewTab;
    $[26] = learnMoreText;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== handleLearnMoreClick || $[29] !== learnMoreTarget || $[30] !== learnMoreUrl || $[31] !== t11) {
    t12 = <>{" "}<a className={styles["learn-more-link"]} data-purpose="subscription-learn-more-link" href={learnMoreUrl} target={learnMoreTarget} onClick={handleLearnMoreClick}>{t11}</a></>;
    $[28] = handleLearnMoreClick;
    $[29] = learnMoreTarget;
    $[30] = learnMoreUrl;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== description || $[34] !== t12) {
    t13 = <div className={t10}>{description}{t12}</div>;
    $[33] = description;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t13 || $[37] !== t9) {
    t14 = <div>{t9}{t13}</div>;
    $[36] = t13;
    $[37] = t9;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  const t15 = CLPPurchaseOption.SUBSCRIPTION === selectedPurchaseOption ? "primary" : "secondary";
  const t16 = parseInt(course.id, 10);
  const t17 = showPPPrice ? unwrappedPersonalPlan : unwrappedConsumerSubscriptionPlan;
  let t18;
  if ($[39] !== showPPPrice || $[40] !== subscriptionCTAConfiguration || $[41] !== subscriptionPriceBoxNotice || $[42] !== unwrappedConsumerSubscriptionPlan) {
    t18 = subscriptionCTAConfiguration?.upgradeUrl ? {
      ...subscriptionPriceBoxNotice,
      cta_1_url: subscriptionCTAConfiguration.upgradeUrl
    } : showPPPrice ? {
      ...subscriptionPriceBoxNotice,
      cta_1_url: `${PLAN_SELECTION_CHECKOUT_URL}?product_type=${unwrappedConsumerSubscriptionPlan.productType}`
    } : subscriptionPriceBoxNotice;
    $[39] = showPPPrice;
    $[40] = subscriptionCTAConfiguration;
    $[41] = subscriptionPriceBoxNotice;
    $[42] = unwrappedConsumerSubscriptionPlan;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  const t19 = subscriptionCTAConfiguration?.directCheckout;
  const t20 = subscriptionCTAConfiguration?.selectPlanText;
  const t21 = subscriptionCTAConfiguration?.purchaseCtaText;
  const t22 = isMobile ? PlanPeriodStyle.MULTILINE_NO_MARGIN : PlanPeriodStyle.MULTILINE_MARGIN;
  let t23;
  if ($[44] !== handleCtaClick || $[45] !== handleOfferImpression || $[46] !== isLoading || $[47] !== t15 || $[48] !== t16 || $[49] !== t17 || $[50] !== t18 || $[51] !== t19 || $[52] !== t20 || $[53] !== t21 || $[54] !== t22) {
    t23 = <SubscriptionCTA className={styles["subscription-cta"]} uiRegion={UI_REGION} subtitleClassName={styles["cta-subtitle"]} data-purpose="subscription-purchase-button" offerCtaStyle={t15} onCTAClick={handleCtaClick} courseId={t16} includeTrialDaysOnCta={false} plan={t17} trackImpression={handleOfferImpression} noticeData={t18} noticeDataLoading={isLoading} directCheckout={t19} selectPlanText={t20} purchaseCtaText={t21} planPeriodStyle={t22} />;
    $[44] = handleCtaClick;
    $[45] = handleOfferImpression;
    $[46] = isLoading;
    $[47] = t15;
    $[48] = t16;
    $[49] = t17;
    $[50] = t18;
    $[51] = t19;
    $[52] = t20;
    $[53] = t21;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== personalPlanValuePropText || $[57] !== showPersonalPlanValueProp) {
    t24 = showPersonalPlanValueProp && <div className={classNames(styles["personal-plan-value-prop"], "ud-text-xs")} data-purpose="subscription-value-prop-text">{personalPlanValuePropText}</div>;
    $[56] = personalPlanValuePropText;
    $[57] = showPersonalPlanValueProp;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== t14 || $[60] !== t23 || $[61] !== t24) {
    t25 = <div data-purpose="subscription-purchase-section">{t14}{t23}{t24}</div>;
    $[59] = t14;
    $[60] = t23;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  return t25;
};
function _temp(contentCollection) {
  return contentCollection.stats;
}